<script setup>
import route from 'ziggy';
import {useForm} from '@inertiajs/vue3';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import Checkbox from '@/Components/Checkbox.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import PublicLayout from '@/Layouts/PublicLayout.vue';
import DaisyLink from '@/Components/Typography/DaisyLink.vue';

defineProps({
    canResetPassword: {
        type: Boolean,
        default: false,
    },
    status: {
        type: String,
        required: true,
    },
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on':'',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<template>
    <PublicLayout :title="$t('menu.login')">

        <AuthenticationCard>
            <template #logo>
                <AuthenticationCardLogo/>
            </template>

            <div v-if="status" class="mb-4 font-medium text-sm text-green-600 dark:text-green-400">
                {{ status }}
            </div>

            <form @submit.prevent="submit">
                <div>
                    <InputLabel for="email" :value="$t('Email')"/>
                    <TextInput
                            id="email"
                            v-model="form.email"
                            type="email"
                            class="mt-1 block"
                            required
                            autofocus
                            autocomplete="username"
                    />
                    <InputError class="mt-2" :message="form.errors.email"/>
                </div>

                <div class="mt-4">
                    <InputLabel for="password" :value="$t('Password')"/>
                    <TextInput
                            id="password"
                            v-model="form.password"
                            type="password"
                            class="mt-1 block"
                            required
                            autocomplete="current-password"
                    />
                    <InputError class="mt-2" :message="form.errors.password"/>
                </div>

                <div class="block mt-4">
                    <label class="flex items-center">
                        <Checkbox v-model:checked="form.remember" name="remember">{{ $t('Remember me') }}</Checkbox>
                    </label>
                </div>

                <div class="flex items-center justify-end mt-4">
                    <DaisyLink :href="route('password.request')">{{ $t('Forgot your password?') }}</DaisyLink>

                    <PrimaryButton class="ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        {{ $t('Log in') }}
                    </PrimaryButton>
                </div>
            </form>
        </AuthenticationCard>
    </PublicLayout>
</template>
